import {
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  LinearProgress
} from '@mui/material';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import useCustomDispatch from 'redux/dispatch';
import { Button } from '@nps.today/component-library';
import AccessGuard, { legacyRoleRules } from 'access';
import { ExternalAuthenticationMethodRow } from './ExternalAuthenticationMethodRow';
import { useTranslator } from 'translation/useTranslator';
import { ExternalAuthentication } from '../../../../@types/externalAuthentication';
import {
  getExternalAuthenticationMethods,
  saveExternalAuthentication
} from 'redux/slices/externalAuthentication';
import { RootState, useSelector } from 'redux/store';
import { ExternalAuthenticationMethodForm } from './ExternalAuthenticationMethodForm';
import { Text } from 'components/text/Text';

const DefaultExternalAuthenticationMethod: ExternalAuthentication = {
  id: null,
  name: '',
  type: 'Basic',
  value: '',
  basicAuthUsername: null,
  customAuthenticationHeaderName: null,
  customAuthenticationHeaderScheme: null,
  linkedWebHooks: [],
  linkedNotificationTriggers: []
};

export default function ExternalAuthenticationMethods() {
  const translator = useTranslator();
  const customDispatch = useCustomDispatch();
  const [createOpen, setCreateOpen] = useState(false);
  const { externalAuthenticationMethods, isLoading } = useSelector(
    (state: RootState) => state.externalAuthenticationMethods
  );
  const [newExternalAuthenticationMethod, setNewExternalAuthenticationMethod] =
    useState<ExternalAuthentication>(DefaultExternalAuthenticationMethod);

  useEffect(() => {
    customDispatch({ action: getExternalAuthenticationMethods, disableSuccessMessage: true });
  }, [customDispatch]);

  return (
    <>
      <CardHeader
        sx={{ py: 0, px: 2 }}
        subheader={
          <>
            <Text variant="body2">{translator.externalAuthenticationMethodsDescription()}</Text>
            <Text variant="body2">
              {translator.youHaveXExternalAuthenticationMethods(
                `${externalAuthenticationMethods.length}`
              )}
            </Text>
          </>
        }
        action={
          <AccessGuard rule={legacyRoleRules.adminManage}>
            <Button
              dataTestId="add-external-authentication-method"
              variant="contained"
              onClick={() => setCreateOpen(true)}
            >
              {translator.addAuthenticationMethod()}
            </Button>
          </AccessGuard>
        }
      />
      <CreateDialog
        externalAuthentication={newExternalAuthenticationMethod}
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onFormChanges={setNewExternalAuthenticationMethod}
      />
      <Stack spacing={2} sx={{ my: 2 }}>
        {isLoading.getExternalAuthenticationMethods ? (
          <LinearProgress variant="indeterminate" />
        ) : (
          <ul style={{ gap: '1rem' }}>
            {externalAuthenticationMethods.map((externalAuthentication, index) => (
              <div key={externalAuthentication.id}>
                <li style={{ listStyle: 'none', gap: '1rem' }}>
                  <ExternalAuthenticationMethodRow
                    externalAuthentication={externalAuthentication}
                  />
                </li>
                {externalAuthenticationMethods.length - 1 > index && (
                  <Divider style={{ margin: '.5rem' }} />
                )}
              </div>
            ))}
          </ul>
        )}
      </Stack>
    </>
  );
}

interface CreateDialogProps {
  externalAuthentication: ExternalAuthentication;
  isOpen: boolean;
  onClose: () => void;
  onFormChanges: (externalAuthentication: ExternalAuthentication) => void;
}

const CreateDialog: FunctionComponent<CreateDialogProps> = ({
  externalAuthentication,
  isOpen,
  onClose,
  onFormChanges
}) => {
  const translator = useTranslator();
  const customDispatch = useCustomDispatch();

  const createDisabled = useMemo(() => {
    const authValueMissing = externalAuthentication.value.length <= 0;
    const nameMissing = externalAuthentication.name.length <= 0;
    const basicAuthUsernameMissing =
      externalAuthentication.type === 'Basic' &&
      (!externalAuthentication.basicAuthUsername ||
        externalAuthentication.basicAuthUsername.length <= 0);
    const customAuthHeaderNameMissing =
      externalAuthentication.type === 'CustomAuthHeader' &&
      (!externalAuthentication.customAuthenticationHeaderName ||
        externalAuthentication.customAuthenticationHeaderName.length <= 0);
    return (
      authValueMissing || nameMissing || basicAuthUsernameMissing || customAuthHeaderNameMissing
    );
  }, [externalAuthentication]);

  const resetForm = () => {
    onClose();
    onFormChanges(DefaultExternalAuthenticationMethod);
  };

  return (
    <Dialog open={isOpen} onClose={resetForm} fullWidth>
      <DialogTitle>{translator.addAuthenticationMethod()}</DialogTitle>
      <DialogContent>
        <ExternalAuthenticationMethodForm
          externalAuthentication={externalAuthentication}
          onUpdate={onFormChanges}
        />
      </DialogContent>
      <DialogActions>
        <Button dataTestId="add-external-authentication-method-dialog-cancel" onClick={resetForm}>
          {translator.cancel()}
        </Button>
        <Button
          dataTestId="add-external-authentication-method-dialog-create"
          disabled={createDisabled}
          onClick={() => {
            if (createDisabled) {
              return;
            }
            customDispatch({
              action: saveExternalAuthentication,
              actionParameters: {
                externalAuthentication: externalAuthentication
              },
              onSuccess: resetForm
            });
          }}
          autoFocus
          variant={'contained'}
        >
          {translator.create()}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
